import {
  Component,
  Input,
  signal,
  computed,
  effect,
  WritableSignal,
  ElementRef,
  HostListener,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  SlidersHorizontal,
  Search,
  LayoutGrid,
  List,
  ListFilter, 
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,Download
} from 'lucide-angular';
import { FinDeNavCbemDto } from '../../Models/CBEM';
import { ClientConfig, ClientNav, ClientNavResponse, FilterConfigType, PaginatedClientNavResponse } from '../../Models/Client-Nav';
import { TableColDto } from '../../Models/Table';
import { animate, style, transition, trigger } from '@angular/animations';
import { ClientNavigatorService } from './client-navigator.service';
import { RootScopeService } from '../../core/services/root-scope.service';
import { StorageService } from '../../core/services/storage.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { checkIcon } from '@progress/kendo-svg-icons';

type ClientNavigatorSortBy = 'new' | 'old' | 'name_desc' | null;

interface ClientNavigatorPreferences {
  sortBy?: ClientNavigatorSortBy;
  appliedFilters?: Record<string, string[]>;
  columnOrder?: string[];
  hiddenColumns?: string[];
}

interface LocalPreferenceStore {
  data: ClientNavigatorPreferences;
  updatedAt?: string;
}

@Component({
  selector: 'app-client-tracker',
  standalone: false,
  templateUrl: './client-navigator.component.html',
  styleUrls: ['./client-navigator.component.scss'],
  animations: [
    trigger('viewSwitch', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('200ms ease-in-out', style({ opacity: 0 })),
      ]),
    ]),
  ],
})

export class ClientTrackerComponent implements OnDestroy {
  private readonly preferenceStorageBaseKey = 'clientNavigator.preferences.v1';
  private preferences: ClientNavigatorPreferences = {};
  private preferencesUpdatedAt?: string;

  // --- Export state ---
  isExporting = false;
  // --- New Client Creation State ---
  newClientModalOpen = false;
  newClientName: string = '';
  newClientProduct: string = '';
  // Multi-select products replacement
  newClientProducts: string[] = [];
  productDropdownOpen = false;
  productSearchTerm: string = '';
  isClientNameFocused = false;
  isProductInputFocused = false;
  isCreatingClient = false;
  creationError: string | null = null;

  // Debounced search subject
  private searchSubject = new Subject<string>();
  private searchDestroyed = false;
  // In-flight client list request; cancelled when a newer one starts so stale responses can't overwrite the grid
  private clientListSub?: Subscription;

  // For dynamic filter dropdown UI
  filterDropdownOpen = false;
  selectedFilterParam: string | null = null; // Track which filter parameter is selected
  // Full products list for new-client modal dropdown 
  private availableProducts: string[] = [];
  @ViewChild('filterWrapper', { static: false }) filterWrapperRef!: ElementRef;
  @ViewChild('searchInput', { static: false }) searchInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('productDropdownWrapper', { static: false }) productDropdownWrapperRef?: ElementRef;
  @ViewChild('productInput', { static: false }) productInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('clientNameInput', { static: false }) clientNameInputRef?: ElementRef<HTMLInputElement>;


  // 👇 Detect clicks anywhere in document
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const target = event.target as Node;
    const clickedInsideFilter = this.filterWrapperRef?.nativeElement?.contains(
      target
    );
    if (!clickedInsideFilter) {
      this.filterDropdownOpen = false;
    }
    const clickedInsideProducts = this.productDropdownWrapperRef?.nativeElement?.contains(
      target
    );
    if (!clickedInsideProducts) {
      this.productDropdownOpen = false;
      this.isProductInputFocused = false;
    }
  }
  // Filter dropdown state
  showFilterDropdown = false;
  sortBy: ClientNavigatorSortBy = null; // null means default alphabetical (name_asc)

  protected readonly Search = Search;
  protected readonly LayoutGrid = LayoutGrid;
  protected readonly List = List;
  protected readonly ChevronDown = ChevronDown;
  protected readonly ChevronLeft = ChevronLeft;
  protected readonly ChevronRight = ChevronRight;
  protected readonly ListFilter = ListFilter;
  protected readonly SlidersHorizontal = SlidersHorizontal;
  protected readonly Plus = Plus;
  protected readonly Download = Download;

  protected isGridView = false;

  @ViewChild('actionsScroll', { static: false }) actionsScrollRef?: ElementRef<HTMLElement>;

  readonly showActionsScrollControls = signal<boolean>(false);
  readonly canScrollActionsLeft = signal<boolean>(false);
  readonly canScrollActionsRight = signal<boolean>(false);

  // Table signal emitted by `verisk-table-component`, used to drive the `column-edit` (Customize View) UI
  protected columnEditTableSignal = signal<WritableSignal<any> | null>(null);

  // Column edit config
  protected readonly columnEditConfig = {
    buttonLabel: 'Customize View',
    buttonLabelIconPath: 'assets/logos/dashboard-customize.svg',
    dragLabelIconPath: 'assets/logos/drag-icon.svg',
    showSearch: false,
    buttonLabelColor: '#00358E',
    checkIcon: '#2A7CE1'
  };

  filtersConfig = signal<FilterConfigType[]>([])

  // signals
  readonly rowSelection = signal<boolean>(false);
  readonly actionMenu = signal<boolean>(true);
  readonly customGlobalFilter = signal<string>('');
  readonly appliedFilters = signal<Record<string, string[]>>({});
  readonly tempFilters = signal<Record<string, string[]>>({});


  public searchValue = signal('');

  public col: TableColDto[] = [
    {
      field: 'clientName',
      label: 'Client',
      order: 1,
      width: 250,
      type: 'link',
    },
    { field: 'state', label: 'State', order: 1, width: 110, type: 'text' },
    {
      field: 'clientBusinessContact',
      label: 'Account Executive',
      order: 1,
      width: 200,
      type: 'text',
    }
  ];

  readonly resRender = signal<ClientNav[]>([]);
  // --- Cursor-based pagination ---
  protected readonly pageSize = 10;
  protected readonly gridCurrentPage = signal<number>(1);
  protected readonly hasNextPage = signal<boolean>(false);
  protected readonly isPageLoading = signal<boolean>(true);
  // Store the LastEvaluatedKey returned for each page so we can navigate back/forward.
  // pageKeys[0] = null (first page needs no cursor), pageKeys[1] = key from page-1 response, etc.
  private pageKeys: (Record<string, string> | null)[] = [null];

  protected readonly gridCurrentPageClamped = computed(() => {
    const maxPage = this.pageKeys.length;
    return Math.min(Math.max(1, this.gridCurrentPage()), maxPage);
  });

  protected readonly gridPaginatedClients = computed(() => {
    return this.filteredClients();
  });

  protected getClientProducts(client: any): { name: string; status: string }[] {
    return this.productGroupNames
      .map((name) => {
        const raw = (client as any)?.[name];
        const status = String(raw ?? '').trim();
        return { name, status };
      })
      .filter((p) => p.status.length > 0);
  }
  readonly colRender = signal<TableColDto[]>(this.col);
  filteredClients = signal<ClientNav[]>([]);
 productGroupNames: string[] = [];
  public allClientsUnsorted: ClientNav[] = [];
  // filteredClients: ClientNav[] = [];

  constructor(
    private clientNavigatorService: ClientNavigatorService,
    private rootScopeService: RootScopeService,
    private storageService: StorageService,
    private router: Router
  ) {
    this.searchSubject.pipe(
      debounceTime(400),
      // Compare against the applied search (not the last emitted value) so re-typing a cleared search still runs
      filter(value => value !== this.searchValue())
    ).subscribe(value => {
      if (!this.searchDestroyed) {
        this.searchValue.set(value);
        this.resetPaginationAndFetch();
      }
    });
  }

  ngOnDestroy() {
    this.searchDestroyed = true;
    this.searchSubject.complete();
    this.clientListSub?.unsubscribe();
  }

  protected gridGoToPage(page: number) {
    const maxPage = this.pageKeys.length;
    if (maxPage <= 0) {
      this.gridCurrentPage.set(1);
      return;
    }
    const clamped = Math.min(Math.max(1, Math.trunc(page)), maxPage);
    this.gridCurrentPage.set(clamped);
    const key = this.pageKeys[clamped - 1] ?? null;
    this.isPageLoading.set(true);
    this.fetchClientList(this.pageSize, key);
  }

  protected gridPageNumbers(): number[] {
    // Show page numbers up to the farthest discovered page
    const total = this.pageKeys.length;
    if (total <= 5) 
      return Array.from({ length: total }, (_, i) => i + 1);
    const current = this.gridCurrentPageClamped();
    let start = Math.max(1, current - 2);
    let end = Math.min(total, start + 4);
    if (end - start < 4) 
      start = Math.max(1, end - 4);
    const pages: number[] = [];
    for (let p = start; p <= end; p++) 
      pages.push(p);
    return pages;
  }

  ngOnInit(): void {
    this.loadPreferences();

    // Load full products and productgroups list for new-client modal
    this.clientNavigatorService.getAllProducts().subscribe({
      next: (res) => {
        this.availableProducts = Object.values(res);
      },
      error: (err) => {
        console.error('Failed to fetch products list', err);
      }
    });

    this.fetchClientList(this.pageSize, null);

    // Load filters config for UI controls. Kept independent of the client list request,
    // which may be cancelled if saved preferences trigger a newer fetch.
    this.clientNavigatorService.get_config_list().subscribe({
      next: (res : ClientConfig[]) => {
        const updatedFilters: FilterConfigType[] = res.map(config => {
          const apiResponseKey = (config as any).configKey || (config as any).configName;
          const normalizedKey = apiResponseKey;
          const label = config.configName;
          return {
            key: normalizedKey,
            label: label,
            type: 'checkbox',
            options: config.configValues,
          };
        });
        this.filtersConfig.set(updatedFilters);
      }
    });
  }

  ngAfterViewInit(): void {
    // Wait one tick for layout so scrollWidth/clientWidth are accurate
    setTimeout(() => this.updateActionsScrollState());
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateActionsScrollState();
  }

  onActionsScroll() {
    this.updateActionsScrollState();
  }

  scrollActions(deltaX: number) {
    const el = this.actionsScrollRef?.nativeElement;
    if (!el) return;
    el.scrollBy({ left: deltaX, behavior: 'smooth' });
  }

  private updateActionsScrollState() {
    const el = this.actionsScrollRef?.nativeElement;
    if (!el) {
      this.showActionsScrollControls.set(false);
      this.canScrollActionsLeft.set(false);
      this.canScrollActionsRight.set(false);
      return;
    }

    const overflow = el.scrollWidth > el.clientWidth + 1;
    this.showActionsScrollControls.set(overflow);

    if (!overflow) {
      this.canScrollActionsLeft.set(false);
      this.canScrollActionsRight.set(false);
      return;
    }

    const canLeft = el.scrollLeft > 0;
    const canRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    this.canScrollActionsLeft.set(canLeft);
    this.canScrollActionsRight.set(canRight);
  }

  private getActiveFilters(): { insuranceTypes?: string[]; productGroups?: string[]; statuses?: string[] } | null {
    const currentFilters = this.appliedFilters();
    if (Object.keys(currentFilters).length === 0) return null;

    const getValues = (keyLower: string): string[] => {
      const originalKey = Object.keys(currentFilters).find(k => k.toLowerCase() === keyLower);
      return originalKey ? (currentFilters[originalKey] || []) : [];
    };

    const insuranceTypes = getValues('insurancetype');
    const productGroups = getValues('productgroups');
    const statuses = getValues('clientproductstatus');

    if (!insuranceTypes.length && !productGroups.length && !statuses.length) return null;

    return {
      ...(insuranceTypes.length ? { insuranceTypes } : {}),
      ...(productGroups.length ? { productGroups } : {}),
      ...(statuses.length ? { statuses } : {}),
    };
  }

  private fetchClientList(take: number, lastEvaluatedKey?: Record<string, string> | null) {
    const search = this.searchValue()?.trim() || null;
    const filters = this.getActiveFilters();
    // Page this request is for, captured now so the cursor is stored against the right page
    const currentPage = this.gridCurrentPage();
    this.clientListSub?.unsubscribe();
    this.clientListSub = this.clientNavigatorService.getClient_list(take, lastEvaluatedKey, this.sortBy, search, filters).subscribe({
      next: (response) => {
        const items = response?.items;
        // Track cursor for next page
        this.hasNextPage.set(response?.hasMorePages ?? !!response?.lastEvaluatedKey);
        if (response?.lastEvaluatedKey) {
          // Store the key needed to fetch the *next* page
          if (this.pageKeys.length <= currentPage) {
            this.pageKeys.push(response.lastEvaluatedKey);
          } else {
            this.pageKeys[currentPage] = response.lastEvaluatedKey;
          }
        }
        if (Array.isArray(items)) {
          // Normalize and derive dynamic product columns from DTO
          const clients = items;

          const productGroupNames = this.getUniqueProductGroupNames(clients);
          this.productGroupNames = productGroupNames;
          // Build columns: base + dynamic product groups
          const baseCols: TableColDto[] = [
            { field: 'clientName', label: 'Client', order: 1, width: 250, type: 'link' },
            { field: 'state', label: 'State', order: 1, width: 110, type: 'text', enableSorting: false },
            { field: 'salesOwner', label: 'Account Executive', order: 1, width: 200, type: 'text', enableSorting: false },
            { field: 's111Owner', label: 'S111 Owner', order: 1, width: 200, type: 'text', enableSorting: false },
          ];

          const productCols: TableColDto[] = productGroupNames.map((name) => ({
            field: name,
            label: name,
            order: 1,
            width: 150,
            type: 'text',
            enableSorting: false,
          }));

          this.col = this.reorderColumnsForPreference([...baseCols, ...productCols]);
          this.colRender.set(this.col);
          // Project product status display onto flat fields per product group
          const projected = clients.map((client: any) => {
            const productMap: Record<string, string> = {};
            const products: any[] = Array.isArray(client.products) ? client.products : client.Products || [];
            products.forEach((p: any) => {
              const groupName = p.productGroupName ?? p.ProductGroupName;
              const statusDisplay = p.productStatusDisplay ?? p.ProductStatusDisplay;
              if (groupName) {
                productMap[groupName] = statusDisplay ?? '-';
              }
            });
            return { ...client, ...productMap };
          });

          this.resRender.set(projected);
          this.filteredClients.set(projected);
          this.isPageLoading.set(false);
        } else {
          this.isPageLoading.set(false);
          console.error('Expected paginated response but got:', response);
        }
      },
      error: (err) => {
        this.isPageLoading.set(false);
        console.error('Failed to fetch client list', err);
      }
    });
  }

  private getUniqueProductGroupNames(clients: any[]): string[] {
    const set = new Set<string>();
    for (const c of clients) {
      const products: any[] = Array.isArray(c.products) ? c.products : c.Products || [];
      for (const p of products) {
        const groupName = p.productGroupName ?? p.ProductGroupName;
        if (groupName) set.add(groupName);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  // --- Filter handling ---

onFilterChange(filterKey: string, option: string, event: any) {
  this.tempFilters.update((prev) => {
    const next = { ...prev };
    
    // Find the filter config to check if it's a radio button
    const filterConfig = this.filtersConfig().find(f => f.key === filterKey);
    
    if (filterConfig?.type === 'radio') {
      // For radio buttons, set only the selected option or remove if unchecked
      if (event.target.checked) {
        next[filterKey] = [option];
      } else {
        delete next[filterKey];
      }
    } else {
      // For checkboxes, use the existing logic
      const values = new Set(next[filterKey] || []);

      if (event.target.checked) {
        values.add(option);
      } else {
        values.delete(option);
      }

      next[filterKey] = Array.from(values);

      // clean up empty
      if (next[filterKey].length === 0) {
        delete next[filterKey];
      }
    }

    return next;
  });
}


applyFilters() {
  this.appliedFilters.set({ ...this.tempFilters() });
  this.filterDropdownOpen = false;
  this.persistPreferences({ appliedFilters: this.appliedFilters() });
  this.resetPaginationAndFetch();
}



  resetFilters() {
    // Ensure search input loses focus to prevent interference
    if (this.searchInputRef?.nativeElement === document.activeElement) {
      this.searchInputRef.nativeElement.blur();
    }
    
    this.appliedFilters.set({});
    this.tempFilters.set({});
      this.searchValue.set('');
    this.persistPreferences({ appliedFilters: {} });
    
    // Re-fetch from API with cleared search
    this.resetPaginationAndFetch();
    this.filterDropdownOpen = false;
  }

  handleRemoveFilter(event: Event) {
    // Prevent any default behavior and stop propagation
    event.preventDefault();
    event.stopPropagation();
    
    // Get the data from the button attributes
    const target = event.target as HTMLButtonElement;
    const filterKey = target.getAttribute('data-filter-key');
    const filterValue = target.getAttribute('data-filter-value');
    
    if (!filterKey || !filterValue) {
      console.error('Missing filter data');
      return;
    }
    
    // Force blur the search input to prevent interference
    if (this.searchInputRef?.nativeElement) {
      this.searchInputRef.nativeElement.blur();
    }
    
    // Call the actual removal method
    this.removeFilter(filterKey, filterValue);
  }

  //  Remove a single filter value (from badge cross) 
removeFilter(filterKey: string, option: string) {
    if(filterKey === 'search'){
      this.searchValue.set('');
      this.resetPaginationAndFetch();
      return;
    }

    // Find the exact key in appliedFilters (handle potential casing issues)
    const appliedFiltersKeys = Object.keys(this.appliedFilters());
    const exactKey = appliedFiltersKeys.find(key => key === filterKey) || filterKey;

    const updateSignal = (sig: typeof this.appliedFilters) => {
      sig.update((prev) => {
        const next = { ...prev };
        if (next[exactKey]) {
          next[exactKey] = next[exactKey].filter((v) => v !== option);
          if (next[exactKey].length === 0) {
            delete next[exactKey];
          }
        }
        return next;
      });
    };

    updateSignal(this.appliedFilters);
    updateSignal(this.tempFilters);

    this.persistPreferences({ appliedFilters: this.appliedFilters() });

    this.resetPaginationAndFetch();
  }

  // --- Expose active filters for UI badges ---
  readonly activeFilters = computed(() => {
    const filterBadges = Object.entries(this.appliedFilters()).map(([key, values]) => ({
      key,
      values,
    }));

    const searchBadge = this.searchValue()
      ? [{ key: 'search', values: [this.searchValue()] }]
      : [];

    return [...filterBadges, ...searchBadge];
  })

onSearchChange(value: string) {
  this.searchSubject.next(value);
}

  toggleSortBy() {
    if (this.sortBy === null || this.sortBy === 'old' || this.sortBy === 'name_desc') {
      this.sortBy = 'new';
    } else if (this.sortBy === 'new') {
      this.sortBy = 'old';
    }
    this.persistPreferences({ sortBy: this.sortBy });
    this.resetPaginationAndFetch();
  }

  onColumnSortChange(sorting: { id: string; desc: boolean }[]) {
    if (!sorting || sorting.length === 0) {
      // Reset to default (name_asc)
      this.sortBy = null;
    } else {
      const sort = sorting[0];
      if (sort.id === 'clientName') {
        this.sortBy = sort.desc ? 'name_desc' : null;
      }
    }
    this.persistPreferences({ sortBy: this.sortBy });
    this.resetPaginationAndFetch();
  }

  private resetPaginationAndFetch() {
    this.pageKeys = [null];
    this.gridCurrentPage.set(1);
    this.isPageLoading.set(true);
    this.fetchClientList(this.pageSize, null);
  }

  onTableSignalReady(tableSignal: WritableSignal<any>) {
    this.columnEditTableSignal.set(tableSignal);
    this.applyColumnPreferencesToTable();
  }

  toggleFilterDropdown() {
    this.filterDropdownOpen = !this.filterDropdownOpen;
    // Set default selected filter param when opening
    if (this.filterDropdownOpen && this.filtersConfig.length > 0) {
      this.selectedFilterParam = this.filtersConfig()[0].key;
    }
  }

  selectFilterParam(paramKey: string) {
    this.selectedFilterParam = paramKey;
  }

  getSelectedFilterConfig() {
    return this.filtersConfig().find(filter => filter.key === this.selectedFilterParam);
  }

  toggleFilter(filterKey: string, option: string) {
    const currentValues = this.appliedFilters()[filterKey] || [];
    let newValues: string[];
    if (currentValues.includes(option)) {
      newValues = currentValues.filter((o) => o !== option);
    } else {
      newValues = [...currentValues, option];
    }
    this.appliedFilters.update((prev) => ({ ...prev, [filterKey]: newValues }));
    this.tempFilters.update((prev) => ({ ...prev, [filterKey]: newValues }));
    this.persistPreferences({ appliedFilters: this.appliedFilters() });
    this.resetPaginationAndFetch();
  }

  openClientDashboard(name: any, id: any, options?: { autoEditFirstSection?: boolean }): void {
    const urlName = name.replace(/\s+/g, '-'); // replace whitespace with hyphens for URL readability
    const queryParams = options?.autoEditFirstSection
      ? { id, autoEditFirstSection: 'true' }
      : { id };
    this.router.navigate(['/ClientNavigator', urlName], { queryParams });
  }

  switchView(isGrid: boolean) {
    this.isGridView = isGrid;
    this.showFilterDropdown = false;
    this.customGlobalFilter.set('');
    this.resetPaginationAndFetch();
  }
  //sends the userid
  onColumnCustomizationChange(preference: { columnOrder: string[]; hiddenColumns: string[] }) {
    this.persistPreferences({
      columnOrder: preference.columnOrder,
      hiddenColumns: preference.hiddenColumns,
    });
  }

  private loadPreferences() {
    const key = this.getPreferenceStorageKey();
    const raw = this.storageService.getLocal<LocalPreferenceStore | ClientNavigatorPreferences>(key, { data: {} });
    // Migrate old shape (preferences stored directly) to new wrapper shape
    const stored: LocalPreferenceStore = 'data' in raw && raw.data !== undefined && typeof raw.data === 'object'
      ? (raw as LocalPreferenceStore)
      : { data: raw as ClientNavigatorPreferences };
    const localPreferences = stored.data ?? {};
    this.preferencesUpdatedAt = stored.updatedAt;
    this.applyPreferences(localPreferences);

    const userId = this.getCurrentUserId();
    if (!userId) {
      return;
    }

    this.clientNavigatorService.getClientNavigatorPreferences<ClientNavigatorPreferences>(userId).subscribe({
      next: (response) => {
        const remotePreferences = response?.data;
        if (!remotePreferences) {
          return;
        }

        if (!this.isRemotePreferenceNewer(this.preferencesUpdatedAt, response.updatedAt)) {
          return;
        }

        this.applyPreferences(remotePreferences);
        this.preferencesUpdatedAt = response.updatedAt;
        this.storageService.setLocal(key, { data: this.preferences, updatedAt: this.preferencesUpdatedAt });
        this.resetPaginationAndFetch();
      },
      error: (err) => {
        console.warn('Failed to load client navigator preferences from server', err);
      }
    });
  }

  private persistPreferences(partialPreference: Partial<ClientNavigatorPreferences>) {
    const updatedAt = new Date().toISOString();
    this.preferences = {
      ...this.preferences,
      ...partialPreference,
    };
    this.preferencesUpdatedAt = updatedAt;
    this.storageService.setLocal(this.getPreferenceStorageKey(), { data: this.preferences, updatedAt });

    const userId = this.getCurrentUserId();
    if (!userId) {
      return;
    }

    this.clientNavigatorService.saveClientNavigatorPreferences<ClientNavigatorPreferences>(userId, {
      data: this.preferences,
      updatedAt,
    }).subscribe({
      error: (err) => {
        console.warn('Failed to sync client navigator preferences to server', err);
      }
    });
  }

  private applyPreferences(preferences: ClientNavigatorPreferences) {
    this.preferences = { ...preferences };

    if (this.preferences.sortBy === 'new' || this.preferences.sortBy === 'old' || this.preferences.sortBy === 'name_desc' || this.preferences.sortBy === null) {
      this.sortBy = this.preferences.sortBy ?? null;
    }

    if (this.preferences.appliedFilters) {
      this.appliedFilters.set({ ...this.preferences.appliedFilters });
      this.tempFilters.set({ ...this.preferences.appliedFilters });
    } else {
      this.appliedFilters.set({});
      this.tempFilters.set({});
    }
  }

  private isRemotePreferenceNewer(localUpdatedAt: string | undefined, remoteUpdatedAt: string | undefined): boolean {
    if (!remoteUpdatedAt) {
      return false;
    }

    if (!localUpdatedAt) {
      return true;
    }

    return new Date(remoteUpdatedAt).getTime() > new Date(localUpdatedAt).getTime();
  }

   private getCurrentUserId(): string | null {
    const rawUserId = this.rootScopeService.globals.currentUserId?.toLowerCase();
    if (!rawUserId) return null;
    return rawUserId.split('@')[0];
  }

    private getPreferenceStorageKey(): string {
    const userId = this.getCurrentUserId() || 'default-user';
    return `${this.preferenceStorageBaseKey}.${userId}`;
  }

  private reorderColumnsForPreference(columns: TableColDto[]): TableColDto[] {
    const preferredOrder = this.preferences.columnOrder;
    if (!preferredOrder || preferredOrder.length === 0) {
      return columns;
    }

    const byField = new Map(columns.map((column) => [column.field, column]));
    const reordered: TableColDto[] = [];

    preferredOrder.forEach((field) => {
      const column = byField.get(field);
      if (column) {
        reordered.push(column);
        byField.delete(field);
      }
    });

    columns.forEach((column) => {
      if (byField.has(column.field)) {
        reordered.push(column);
      }
    });

    return reordered;
  }

  public applyColumnPreferencesToTable() {
    const tableSignal = this.columnEditTableSignal();
    if (!tableSignal) {
      return;
    }

    const table = tableSignal();
    const allColumns = table
      .getAllLeafColumns()
      .filter((col: any) => col.id !== 'select' && col.id !== 'action')
      .map((col: any) => col.id);

    if (this.preferences.columnOrder && this.preferences.columnOrder.length > 0) {
      const preferredOrder = this.preferences.columnOrder.filter((field) => allColumns.includes(field));
      if (preferredOrder.length > 0) {
        table.setColumnOrder(preferredOrder);
      }
    }

    if (this.preferences.hiddenColumns) {
      const hiddenSet = new Set(this.preferences.hiddenColumns);
      const visibilityMap: Record<string, boolean> = {};
      allColumns.forEach((columnId: string) => {
        visibilityMap[columnId] = !hiddenSet.has(columnId);
      });
      table.setColumnVisibility(visibilityMap);
    }
  }

  exportClientsData(){
    if (this.isExporting) return;
    this.isExporting = true;
    this.clientNavigatorService.getPresignedUrl().subscribe({
      next: (response) => {
        if (response && response.downloadURL) {
          window.open(response.downloadURL, '_blank');
        }
        this.isExporting = false;
      },
      error: (err) => {
        console.error('Failed to get presigned URL', err);
        this.isExporting = false;
      },
      complete: () => {
        this.isExporting = false;
      }
    });
  }

  // --- New Client Modal Controls ---
  openNewClientModal() {
    this.newClientModalOpen = true;
    this.creationError = null;
    this.newClientName = '';
    this.newClientProduct = '';
    this.newClientProducts = [];
    this.productSearchTerm = '';
    this.productDropdownOpen = false;
    this.isClientNameFocused = false;
    this.isProductInputFocused = false;
  }

  closeNewClientModal() {
    if (this.isCreatingClient) return; // prevent closing during create
    this.newClientModalOpen = false;
    this.creationError = null;
    this.newClientProducts = [];
    this.productDropdownOpen = false;
    this.productSearchTerm = '';
    this.isClientNameFocused = false;
    this.isProductInputFocused = false;
  }

  canCreateClient(): boolean {
    return !!this.newClientName.trim() && this.newClientProducts.length > 0 && !this.isCreatingClient;
  }

  createClient() {
    if (!this.canCreateClient()) return;
    this.isCreatingClient = true;
    this.creationError = null;
    const payload = {
      clientName: this.newClientName.trim(),
      // keep single product for backward compatibility while also sending full list
      product: this.newClientProducts[0],
      userId: this.rootScopeService.globals.currentUserId!,
      products: this.newClientProducts
    };
    this.clientNavigatorService.createClient(payload).subscribe({
      next: (created) => {
        if(created.status == "Duplicate"){
          this.creationError = 'Client name is already present';
          console.error('Create client error',created);
          this.isCreatingClient = false;
          return;
        }
        // Update local lists if response provides data
        if (created) {
          const updated = [...this.resRender(), created].sort((a,b) => a.clientName.localeCompare(b.clientName));
          this.resRender.set(updated);
          this.filteredClients.set(updated);
          // Navigate to client info/dashboard
          this.openClientDashboard(created.clientName, (created as any).id, { autoEditFirstSection: true });
        }
        this.isCreatingClient = false;
        this.newClientModalOpen = false;
      },
      error: (err) => {
        this.creationError = 'Failed to create client';
        console.error('Create client error', err);
        this.isCreatingClient = false;
      }
    });
  }


  get filteredNewClientProducts(): string[] {
    const term = this.productSearchTerm.trim().toLowerCase();
    return this.availableProducts.filter(p => !this.newClientProducts.includes(p) && (!term || p.toLowerCase().includes(term)));
  }

  toggleProductDropdown() {
    this.productDropdownOpen = !this.productDropdownOpen;
    if (this.productDropdownOpen) {
      this.productSearchTerm = '';
      this.isProductInputFocused = true;
      this.focusProductInput();
    } else {
      this.isProductInputFocused = false;
    }
  }

  addNewClientProduct(p: string) {
    if (!p || this.newClientProducts.includes(p)) return;
    this.newClientProducts.push(p);
    this.productSearchTerm = '';
    this.productDropdownOpen = true;
    this.focusProductInput();
  }

  removeNewClientProduct(index: number) {
    if (index < 0 || index >= this.newClientProducts.length) return;
    this.newClientProducts.splice(index,1);
  }

  onNewClientProductSearch(value: string) {
    this.productSearchTerm = value;
    this.productDropdownOpen = true;
  }

  onClientNameFocus() {
    this.isClientNameFocused = true;
  }

  onClientNameBlur() {
    this.isClientNameFocused = false;
  }

  onProductInputFocus() {
    this.isProductInputFocused = true;
    this.productDropdownOpen = true;
  }

  onProductInputBlur() {
    setTimeout(() => {
      if (!this.productDropdownOpen) {
        this.isProductInputFocused = false;
      }
    }, 100);
  }

  get clientNameLabelFloating(): boolean {
    return this.isClientNameFocused || !!this.newClientName.trim();
  }

  get productLabelFloating(): boolean {
    return (
      this.isProductInputFocused ||
      !!this.productSearchTerm.trim() ||
      this.productDropdownOpen ||
      this.newClientProducts.length > 0
    );
  }

  focusProductInput() {
    if (this.productInputRef?.nativeElement) {
      this.productInputRef.nativeElement.focus();
    }
    this.isProductInputFocused = true;
    this.productDropdownOpen = true;
  }

  focusClientName() {
    this.clientNameInputRef?.nativeElement?.focus();
  }

  onCellChange(event: {
    field: string;
    value: any;
    rowData: any;
    eventType: string;
  }) {
    if (event.eventType === 'click') {
      this.openClientDashboard(event.rowData.clientName,event.rowData.id);
    }
  }
}
